import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthRedirectPaths } from '../../utils/authRedirect';
import Sidebar from './Sidebar';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { loginPath, registerPath } = getAuthRedirectPaths(location);
  const { user, userProfile, signOut } = useAuth();

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: '/', 
      icon: 'Home',
      tooltip: 'Single email validation and quick access'
    },
    { 
      label: 'Pricing', 
      path: '/pricing', 
      icon: 'DollarSign',
      tooltip: 'View pricing plans and features'
    },
    {
      label: 'API Docs',
      path: '/docs',
      icon: 'Code',
      tooltip: 'Integration documentation'
    },
    {
      label: 'Blog',
      path: '/blog',
      icon: 'BookOpen',
      tooltip: 'Read our latest articles'
    }
  ];

  // Add admin dashboard link for admin users
  if (userProfile?.role === 'admin') {
    navigationItems.push({
      label: 'Admin',
      path: '/admin-dashboard',
      icon: 'Shield',
      tooltip: 'Access admin panel'
    });
  }

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header className="bg-surface border-b border-border sticky top-0 z-100">
      <div className="mx-auto px-6">
        {/* Top Header Bar */}
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-md">
              <Icon name="Mail" size={20} color="white" />
            </div>
            <span className="text-xl font-semibold text-text-primary font-heading">
              EmailValidator
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item flex items-center space-x-2 rounded-md ${
                  isActiveRoute(item.path) ? 'active bg-primary/5' : 'hover:bg-gray-50'
                }`}
                title={item.tooltip}
              >
                <Icon name={item.icon} size={18} />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Auth Buttons - Desktop */}
          <div className="hidden md:flex items-center space-x-3">
            {!user && (
              <div className="flex items-center space-x-2">
                <Link to={loginPath}>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="LogIn"
                    iconSize={16}
                    animation="slide"
                  >
                    Login
                  </Button>
                </Link>
                <Link to={registerPath}>
                  <Button
                    variant="primary"
                    size="sm"
                    iconName="UserPlus"
                    iconSize={16}
                    animation="bounce"
                  >
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Sidebar Trigger */}
          <div className="hidden md:block">
            <Sidebar />
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            className="md:hidden"
            onClick={toggleMobileMenu}
            iconName={isMobileMenuOpen ? 'X' : 'Menu'}
            iconSize={20}
            animation="rotate"
          >
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-surface">
            <nav className="py-4 space-y-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-md mx-2 ${
                    isActiveRoute(item.path)
                      ? 'text-primary bg-primary/5' :'text-text-secondary hover:text-primary hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Icon name={item.icon} size={18} />
                  <span>{item.label}</span>
                </Link>
              ))}
              
              {/* Auth Buttons - Mobile */}
              <div className="px-4 py-3 border-t border-border">
                {!user && (
                  <div className="space-y-2">
                    <Link to={loginPath} onClick={() => setIsMobileMenuOpen(false)}>
                      <Button
                        variant="ghost"
                        size="sm"
                        fullWidth
                        iconName="LogIn"
                        iconSize={16}
                        animation="slide"
                      >
                        Login
                      </Button>
                    </Link>
                    <Link to={registerPath} onClick={() => setIsMobileMenuOpen(false)}>
                      <Button
                        variant="primary"
                        size="sm"
                        fullWidth
                        iconName="UserPlus"
                        iconSize={16}
                        animation="bounce"
                      >
                        Sign Up
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;